/**
 * collection-filters.js  —  Venisons Heritage 2026
 * Client-side filtering for collection pages.
 *
 * Reads data attributes on each .product-card:
 *   data-type      — product type string
 *   data-tags      — comma-separated full tags  e.g. "Colour:Navy Blue,Fabric:Jacquard"
 *   data-price     — integer pence/cents value  (uses money integer stored by Liquid)
 *   data-sizes     — comma-separated size strings e.g. "36,38,40"
 *
 * Active state is stored in a plain JS object; no URL manipulation,
 * so pagination is intentionally reset on filter change (client-side scope).
 */

(function () {
  'use strict';

  /* --------------------------------------------------------
     State
  -------------------------------------------------------- */
  const state = {
    type: new Set(),
    colour: new Set(),
    size: new Set(),
    fabric: new Set(),
    occasion: new Set(),
    priceMin: null,
    priceMax: null,
  };

  /* --------------------------------------------------------
     DOM references (populated in init)
  -------------------------------------------------------- */
  let grid, cards, noResults, clearAllBtns, mobileBadge, mobileCountEl;

  /* --------------------------------------------------------
     Helpers
  -------------------------------------------------------- */
  function totalActive() {
    return (
      state.type.size +
      state.colour.size +
      state.size.size +
      state.fabric.size +
      state.occasion.size +
      (state.priceMin !== null || state.priceMax !== null ? 1 : 0)
    );
  }

  function parsePrice(card) {
    // data-price is stored as an integer in the store's base unit
    // e.g. pence for GBP: £14.99 → 1499
    return parseInt(card.dataset.price || '0', 10);
  }

  function cardMatchesState(card) {
    const type    = (card.dataset.type || '').trim();
    const tagsRaw = (card.dataset.tags || '');
    const tags    = tagsRaw ? tagsRaw.split(',').map(t => t.trim()) : [];
    const price   = parsePrice(card);
    const sizes   = (card.dataset.sizes || '').split(',').map(s => s.trim()).filter(Boolean);

    // Category / product type
    if (state.type.size > 0 && !state.type.has(type)) return false;

    // Colour
    if (state.colour.size > 0) {
      const colourTags = tags
        .filter(t => t.startsWith('Colour:'))
        .map(t => t.replace('Colour:', '').trim());
      if (!colourTags.some(c => state.colour.has(c))) return false;
    }

    // Fabric
    if (state.fabric.size > 0) {
      const fabricTags = tags
        .filter(t => t.startsWith('Fabric:'))
        .map(t => t.replace('Fabric:', '').trim());
      if (!fabricTags.some(f => state.fabric.has(f))) return false;
    }

    // Occasion
    if (state.occasion.size > 0) {
      const occasionTags = tags
        .filter(t => t.startsWith('Occasion:'))
        .map(t => t.replace('Occasion:', '').trim());
      if (!occasionTags.some(o => state.occasion.has(o))) return false;
    }

    // Size
    if (state.size.size > 0) {
      if (!sizes.some(s => state.size.has(s))) return false;
    }

    // Price (stored as minor units — divide by 100 for comparison with GBP)
    const priceGBP = price / 100;
    if (state.priceMin !== null && priceGBP < state.priceMin) return false;
    if (state.priceMax !== null && priceGBP > state.priceMax) return false;

    return true;
  }

  /* --------------------------------------------------------
     Apply filters to the grid
  -------------------------------------------------------- */
  function applyFilters() {
    let visible = 0;
    cards.forEach(card => {
      const show = cardMatchesState(card);
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });

    // No-results message
    if (noResults) {
      noResults.classList.toggle('visible', visible === 0);
    }

    // Clear-all button visibility
    const hasActive = totalActive() > 0;
    clearAllBtns.forEach(btn => btn.classList.toggle('visible', hasActive));

    // Mobile badge
    if (mobileBadge) {
      const count = totalActive();
      mobileBadge.textContent = count;
      mobileBadge.classList.toggle('visible', count > 0);
    }

    // Mobile results count text
    if (mobileCountEl) {
      mobileCountEl.textContent = visible + ' result' + (visible !== 1 ? 's' : '');
    }

    // Update active chips
    renderChips();
  }

  /* --------------------------------------------------------
     Active chips
  -------------------------------------------------------- */
  function renderChips() {
    const containers = document.querySelectorAll('.filter-active-chips');
    containers.forEach(container => {
      container.innerHTML = '';

      const addChip = (label, removeFn) => {
        const chip = document.createElement('span');
        chip.className = 'filter-chip';
        chip.innerHTML = `${label} <span class="filter-chip-remove" aria-hidden="true">&times;</span>`;
        chip.addEventListener('click', () => { removeFn(); applyFilters(); syncCheckboxes(); });
        container.appendChild(chip);
      };

      state.type.forEach(v    => addChip(v,              () => state.type.delete(v)));
      state.colour.forEach(v  => addChip(v,              () => state.colour.delete(v)));
      state.fabric.forEach(v  => addChip(v,              () => state.fabric.delete(v)));
      state.occasion.forEach(v=> addChip(v,              () => state.occasion.delete(v)));
      state.size.forEach(v    => addChip('Size ' + v,    () => state.size.delete(v)));

      if (state.priceMin !== null || state.priceMax !== null) {
        const label = buildPriceLabel();
        addChip(label, () => { state.priceMin = null; state.priceMax = null; syncPriceInputs(); });
      }
    });
  }

  function buildPriceLabel() {
    if (state.priceMin !== null && state.priceMax !== null) {
      return `£${state.priceMin}–£${state.priceMax}`;
    } else if (state.priceMin !== null) {
      return `£${state.priceMin}+`;
    } else if (state.priceMax !== null) {
      return `Under £${state.priceMax}`;
    }
    return 'Price';
  }

  /* --------------------------------------------------------
     Sync checkboxes to state (used when chips are removed)
  -------------------------------------------------------- */
  function syncCheckboxes() {
    document.querySelectorAll('.filter-option-item input[type="checkbox"]').forEach(cb => {
      const group = cb.dataset.group;
      const value = cb.dataset.value;
      if (!group || !value) return;
      const set = state[group];
      cb.checked = set ? set.has(value) : false;
    });
  }

  function syncPriceInputs() {
    document.querySelectorAll('.filter-price-input[data-role="min"]')
      .forEach(el => { el.value = state.priceMin !== null ? state.priceMin : ''; });
    document.querySelectorAll('.filter-price-input[data-role="max"]')
      .forEach(el => { el.value = state.priceMax !== null ? state.priceMax : ''; });
  }

  /* --------------------------------------------------------
     Clear all
  -------------------------------------------------------- */
  function clearAll() {
    state.type.clear();
    state.colour.clear();
    state.size.clear();
    state.fabric.clear();
    state.occasion.clear();
    state.priceMin = null;
    state.priceMax = null;
    syncCheckboxes();
    syncPriceInputs();
    applyFilters();
  }

  /* --------------------------------------------------------
     Wire checkboxes
  -------------------------------------------------------- */
  function wireCheckbox(checkbox) {
    checkbox.addEventListener('change', () => {
      const group = checkbox.dataset.group;
      const value = checkbox.dataset.value;
      if (!group || !value || !state[group]) return;
      if (checkbox.checked) {
        state[group].add(value);
      } else {
        state[group].delete(value);
      }
      applyFilters();
    });
  }

  /* --------------------------------------------------------
     Collapsible filter groups
  -------------------------------------------------------- */
  function wireGroupToggle(toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      const group = toggleBtn.closest('.filter-group');
      if (group) group.classList.toggle('collapsed');
    });
  }

  /* --------------------------------------------------------
     Show-more within a group
  -------------------------------------------------------- */
  function wireShowMore(btn) {
    const optionsList = btn.previousElementSibling;
    btn.addEventListener('click', () => {
      const expanded = optionsList.classList.toggle('show-all');
      btn.textContent = expanded ? '— Show less' : '+ Show more';
    });
  }

  /* --------------------------------------------------------
     Price inputs
  -------------------------------------------------------- */
  function wirePriceInputs() {
    document.querySelectorAll('.filter-price-input').forEach(input => {
      input.addEventListener('change', () => {
        const role = input.dataset.role;
        const val  = input.value !== '' ? parseFloat(input.value) : null;
        if (role === 'min') state.priceMin = val;
        if (role === 'max') state.priceMax = val;
        applyFilters();
      });
    });
  }

  /* --------------------------------------------------------
     Price preset checkboxes
  -------------------------------------------------------- */
  function wirePricePresets() {
    document.querySelectorAll('.filter-price-preset').forEach(cb => {
      cb.addEventListener('change', () => {
        // Uncheck siblings first
        document.querySelectorAll('.filter-price-preset').forEach(other => {
          if (other !== cb) other.checked = false;
        });
        if (cb.checked) {
          const min = cb.dataset.min !== '' ? parseFloat(cb.dataset.min) : null;
          const max = cb.dataset.max !== '' ? parseFloat(cb.dataset.max) : null;
          state.priceMin = min;
          state.priceMax = max;
        } else {
          state.priceMin = null;
          state.priceMax = null;
        }
        syncPriceInputs();
        applyFilters();
      });
    });
  }

  /* --------------------------------------------------------
     Sort select (already in collection-grid but re-wired here
     for the mobile drawer copy if present)
  -------------------------------------------------------- */
  function wireSortSelects() {
    document.querySelectorAll('[data-sort-select]').forEach(sel => {
      sel.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        window.location.href = url.toString();
      });
    });
  }

  /* --------------------------------------------------------
     Mobile drawer
  -------------------------------------------------------- */
  function initMobileDrawer() {
    const trigger  = document.querySelector('.filter-mobile-trigger');
    const drawer   = document.querySelector('.filter-drawer');
    const overlay  = document.querySelector('.filter-overlay');
    const closeBtn = document.querySelector('.filter-drawer-close');
    const applyBtn = document.querySelector('[data-filter-apply]');
    const clearBtn = document.querySelector('[data-filter-clear-drawer]');

    if (!trigger || !drawer) return;

    const open  = () => { drawer.classList.add('active'); overlay.classList.add('active'); document.body.style.overflow = 'hidden'; };
    const close = () => { drawer.classList.remove('active'); overlay.classList.remove('active'); document.body.style.overflow = ''; };

    trigger.addEventListener('click', open);
    if (closeBtn)  closeBtn.addEventListener('click', close);
    if (overlay)   overlay.addEventListener('click', close);
    if (applyBtn)  applyBtn.addEventListener('click', close);
    if (clearBtn)  clearBtn.addEventListener('click', () => { clearAll(); close(); });
  }

  /* --------------------------------------------------------
     Build option counts from live product data
  -------------------------------------------------------- */
  function computeCounts() {
    const counts = {
      type:    {},
      colour:  {},
      fabric:  {},
      occasion:{},
      size:    {},
    };

    cards.forEach(card => {
      const type = (card.dataset.type || '').trim();
      if (type) counts.type[type] = (counts.type[type] || 0) + 1;

      const tags = (card.dataset.tags || '').split(',').map(t => t.trim()).filter(Boolean);
      tags.forEach(tag => {
        if (tag.startsWith('Colour:')) {
          const v = tag.replace('Colour:', '').trim();
          counts.colour[v] = (counts.colour[v] || 0) + 1;
        }
        if (tag.startsWith('Fabric:')) {
          const v = tag.replace('Fabric:', '').trim();
          counts.fabric[v] = (counts.fabric[v] || 0) + 1;
        }
        if (tag.startsWith('Occasion:')) {
          const v = tag.replace('Occasion:', '').trim();
          counts.occasion[v] = (counts.occasion[v] || 0) + 1;
        }
      });

      const sizes = (card.dataset.sizes || '').split(',').map(s => s.trim()).filter(Boolean);
      sizes.forEach(s => { counts.size[s] = (counts.size[s] || 0) + 1; });
    });

    return counts;
  }

  function updateCountBadges(counts) {
    document.querySelectorAll('.filter-option-count[data-group][data-value]').forEach(badge => {
      const group = badge.dataset.group;
      const value = badge.dataset.value;
      const n = (counts[group] && counts[group][value]) || 0;
      badge.textContent = n;
      // Hide options with zero products
      const item = badge.closest('.filter-option-item');
      if (item) item.style.display = n === 0 ? 'none' : '';
    });
  }

  /* --------------------------------------------------------
     Show-more: hide options beyond 8
  -------------------------------------------------------- */
  function initShowMore() {
    document.querySelectorAll('.filter-options').forEach(list => {
      const items = list.querySelectorAll('.filter-option-item');
      if (items.length <= 8) return;
      items.forEach((item, i) => {
        if (i >= 8) item.classList.add('hidden-option');
      });
      const btn = list.nextElementSibling;
      if (btn && btn.classList.contains('filter-show-more')) {
        btn.classList.add('visible');
      }
    });
  }

  /* --------------------------------------------------------
     Deep-link: pre-apply filters from URL params
     e.g. /collections/clearance?type=Sherwani  (Sale-by-Category menu)
          ?occasion=Wedding  ?colour=Ivory  ?fabric=Jacquard  ?size=40
     Degrades gracefully: unknown values simply do nothing.
  -------------------------------------------------------- */
  function applyUrlFilters() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const groups = { type: 'type', colour: 'colour', color: 'colour', fabric: 'fabric', occasion: 'occasion', size: 'size' };
    Object.keys(groups).forEach(function (key) {
      const raw = params.get(key);
      if (!raw) return;
      raw.split(',').forEach(function (val) {
        const v = val.trim();
        if (!v) return;
        const sel = '.filter-option-item input[type="checkbox"][data-group="' + groups[key] + '"][data-value="' + v.replace(/"/g, '\\"') + '"]';
        const cb = document.querySelector(sel);
        if (cb && !cb.checked) {
          cb.checked = true;
          cb.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* --------------------------------------------------------
     Init
  -------------------------------------------------------- */
  function init() {
    grid      = document.querySelector('.collection-grid');
    noResults = document.querySelector('.collection-no-results');
    clearAllBtns = Array.from(document.querySelectorAll('.filter-clear-all'));
    mobileBadge  = document.querySelector('.filter-mobile-badge');
    mobileCountEl= document.querySelector('.filter-results-count');

    if (!grid) return;

    cards = Array.from(grid.querySelectorAll('.product-card'));

    if (!cards.length) return;

    // Wire checkboxes (covers both sidebar and drawer — same DOM for desktop;
    // mobile drawer uses a separate panel rendered by the section)
    document.querySelectorAll('.filter-option-item input[type="checkbox"]')
      .forEach(wireCheckbox);

    // Collapsible groups
    document.querySelectorAll('.filter-group-toggle').forEach(wireGroupToggle);

    // Show-more
    document.querySelectorAll('.filter-show-more').forEach(wireShowMore);

    // Price inputs
    wirePriceInputs();
    wirePricePresets();

    // Sort
    wireSortSelects();

    // Clear all
    clearAllBtns.forEach(btn => btn.addEventListener('click', clearAll));

    // Mobile drawer
    initMobileDrawer();

    // Count badges
    const counts = computeCounts();
    updateCountBadges(counts);

    // Show-more truncation
    initShowMore();

    // Pre-apply any filters passed via URL (Sale-by-Category links, etc.)
    applyUrlFilters();

    // Initial render
    applyFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
